import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { GitBranch } from "lucide-react";

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

// Very light-weight Sankey layout: three columns (income · hub · expenses),
// with link ribbons drawn as SVG cubic-Bezier paths whose thickness is
// proportional to `link.value`. No external dep — keeps the bundle lean.
export default function CashFlowSankey({ className = "" }) {
    const { language } = useApp();
    const isEs = language === "es";
    const [data, setData] = useState(null);
    const [hoverLink, setHoverLink] = useState(null);

    useEffect(() => {
        api.get("/planning/sankey").then((r) => setData(r.data)).catch(() => setData(null));
    }, []);

    const layout = useMemo(() => {
        if (!data?.nodes?.length) return null;

        const W = 720, H = 380;
        const colX = { income: 30, hub: W / 2, expense: W - 30, saved: W - 30 };
        const hubIdx = data.nodes.findIndex((n) => n.kind === "hub");
        const incomeNodes = data.nodes.filter((n) => n.kind === "income");
        const expenseNodes = data.nodes.filter((n) => n.kind === "expense" || n.kind === "saved");

        const totalIncome = data.totals.income || 1;
        const totalOut = (data.totals.expenses + data.totals.saved) || 1;
        const scale = (H - 40) / Math.max(totalIncome, totalOut);

        // Assign vertical positions proportional to value (min 22px per node so
        // labels don't overlap on tiny slices).
        const MIN_H = 22;
        let yCursor = 20;
        const posByIdx = {};
        incomeNodes.forEach((n) => {
            const idx = data.nodes.indexOf(n);
            const outVal = data.links.filter((l) => l.source === idx).reduce((s, l) => s + l.value, 0);
            const h = Math.max(MIN_H, outVal * scale);
            posByIdx[idx] = { x: colX.income, y: yCursor, h, side: "left" };
            yCursor += h + 6;
        });

        // Hub node — one big bar centered
        const hubH = Math.max(40, totalIncome * scale);
        posByIdx[hubIdx] = { x: colX.hub, y: (H - hubH) / 2, h: hubH, side: "hub" };

        yCursor = 20;
        expenseNodes.forEach((n) => {
            const idx = data.nodes.indexOf(n);
            const inVal = data.links.filter((l) => l.target === idx).reduce((s, l) => s + l.value, 0);
            const h = Math.max(MIN_H, inVal * scale);
            posByIdx[idx] = {
                x: n.kind === "saved" ? colX.saved : colX.expense,
                y: yCursor,
                h,
                side: "right",
                kind: n.kind,
            };
            yCursor += h + 6;
        });

        // Compute link paths — each link stacks vertically at both endpoints.
        // Track how much of each endpoint has been consumed.
        const consumedBySource = {};
        const consumedByTarget = {};
        const linkPaths = data.links.map((l, i) => {
            const src = posByIdx[l.source];
            const tgt = posByIdx[l.target];
            if (!src || !tgt) return null;
            const thickness = Math.max(1.2, l.value * scale);
            const yS = src.y + (consumedBySource[l.source] || 0) + thickness / 2;
            const yT = tgt.y + (consumedByTarget[l.target] || 0) + thickness / 2;
            consumedBySource[l.source] = (consumedBySource[l.source] || 0) + thickness;
            consumedByTarget[l.target] = (consumedByTarget[l.target] || 0) + thickness;

            const x1 = src.x + (src.side === "left" ? 10 : 6);
            const x2 = tgt.x - (tgt.side === "right" ? 10 : 6);
            const cX = (x1 + x2) / 2;
            const d = `M ${x1},${yS} C ${cX},${yS} ${cX},${yT} ${x2},${yT}`;
            const color = tgt.kind === "saved"
                ? "rgba(16,185,129,0.55)"
                : (src.side === "left" ? "rgba(139,92,246,0.42)" : "rgba(244,114,182,0.42)");
            return { i, d, thickness, color, value: l.value, srcNode: data.nodes[l.source], tgtNode: data.nodes[l.target] };
        }).filter(Boolean);

        return { W, H, posByIdx, linkPaths, hubIdx };
    }, [data]);

    if (!data || !data.nodes.length) {
        return (
            <div className={`rounded-3xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-6 ${className}`}
                 data-testid="sankey-empty">
                <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] text-xs uppercase tracking-widest font-bold mb-2">
                    <GitBranch className="w-4 h-4" /> {isEs ? "Flujo de tu dinero" : "Where your money flows"}
                </div>
                <div className="text-[hsl(var(--muted-foreground))] text-sm py-6 text-center border border-dashed border-[hsl(var(--border))] rounded-xl">
                    {isEs ? "Conecta tu banco para ver tu diagrama de flujo." : "Connect your bank to see your cash-flow diagram."}
                </div>
            </div>
        );
    }

    const { W, H, posByIdx, linkPaths, hubIdx } = layout;

    return (
        <div className={`rounded-3xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-5 md:p-7 ${className}`}
             data-testid="sankey-card">
            <div className="flex items-baseline justify-between gap-3 mb-3">
                <div>
                    <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] text-xs uppercase tracking-widest font-bold">
                        <GitBranch className="w-4 h-4" /> {isEs ? "Flujo de tu dinero (30 días)" : "Where your money flows (30 days)"}
                    </div>
                    <div className="mt-1 font-display font-extrabold text-2xl md:text-3xl">
                        {fmt(data.totals.income)} <span className="text-[hsl(var(--muted-foreground))] text-sm font-medium">
                            {isEs ? "entró" : "in"} · </span>
                        <span className="text-emerald-500">{fmt(data.totals.saved)}</span>
                        <span className="text-[hsl(var(--muted-foreground))] text-sm font-medium"> {isEs ? "quedó" : "left over"}</span>
                    </div>
                </div>
            </div>

            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[380px] block" preserveAspectRatio="xMidYMid meet">
                {/* Ribbons first, so labels sit on top */}
                {linkPaths.map((l) => (
                    <path
                        key={l.i}
                        d={l.d}
                        stroke={l.color}
                        strokeWidth={l.thickness}
                        fill="none"
                        opacity={hoverLink === null || hoverLink === l.i ? 1 : 0.25}
                        onMouseEnter={() => setHoverLink(l.i)}
                        onMouseLeave={() => setHoverLink(null)}
                        style={{ transition: "opacity 0.15s" }}
                    />
                ))}
                {/* Nodes */}
                {data.nodes.map((n, idx) => {
                    const p = posByIdx[idx];
                    if (!p) return null;
                    const barW = idx === hubIdx ? 16 : 10;
                    const bg = n.kind === "income" ? "#8B5CF6"
                        : n.kind === "hub" ? "#0EA5E9"
                        : n.kind === "saved" ? "#10B981" : "#F472B6";
                    const isRight = p.side === "right";
                    const labelX = isRight ? p.x + barW + 6 : p.x - barW - 6;
                    const total = data.links
                        .filter((l) => l.source === idx || l.target === idx)
                        .reduce((s, l) => s + l.value, 0);
                    const displayVal = idx === hubIdx ? data.totals.income : total;
                    return (
                        <g key={n.id}>
                            <rect
                                x={p.x - barW / 2}
                                y={p.y}
                                width={barW}
                                height={p.h}
                                rx={3}
                                fill={bg}
                            />
                            <text
                                x={labelX}
                                y={p.y + p.h / 2 - 2}
                                textAnchor={isRight ? "start" : "end"}
                                fontSize="12"
                                fontWeight="700"
                                fill="currentColor"
                                className="text-[hsl(var(--foreground))]"
                            >
                                {n.label}
                            </text>
                            <text
                                x={labelX}
                                y={p.y + p.h / 2 + 12}
                                textAnchor={isRight ? "start" : "end"}
                                fontSize="10.5"
                                fill="currentColor"
                                className="text-[hsl(var(--muted-foreground))]"
                            >
                                {fmt(displayVal)}
                            </text>
                        </g>
                    );
                })}
            </svg>

            <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-[hsl(var(--muted-foreground))]">
                <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm inline-block" style={{ background: "#8B5CF6" }} />
                    {isEs ? "Ingresos" : "Income"}
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm inline-block" style={{ background: "#F472B6" }} />
                    {isEs ? "Gastos" : "Expenses"}
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm inline-block" style={{ background: "#10B981" }} />
                    {isEs ? "Ahorrado" : "Saved"}
                </span>
            </div>
        </div>
    );
}
